import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ArrowRight, CalendarClock, CheckCircle2, LoaderCircle } from 'lucide-react'
import { toast } from 'sonner'

import { assignmentsApi } from '@/services/assignments'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { JobTitleLink } from '@/components/jobs/job-title-link'
import { getErrorMessage } from '@/lib/api'
import { isTerminalAssignmentStatus } from '@/lib/assignment-status'
import { getWorkDayEntryStatusLabel } from '@/lib/workday-entry-status'
import { withServiceInstanceId } from '@/lib/service-instance'
import { withReturnTo } from '@/lib/return-navigation'
import { formatDate } from '@/lib/utils'
import type { WorkDayEntry } from '@/types'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'

interface WorkDayEntriesAttentionProps {
  count?: number
  isLoading?: boolean
  serviceInstanceId?: string | null
  returnPath?: string | null
  jobId?: string | null
  className?: string
}

const formatShortId = (value: string | number | undefined | null) => String(value ?? '').slice(0, 8)

export function WorkDayEntriesAttention({
  count = 0,
  isLoading = false,
  serviceInstanceId,
  returnPath,
  jobId,
  className = '',
}: WorkDayEntriesAttentionProps) {
  const labels = useWorkspaceLabels()
  const [open, setOpen] = useState(false)
  const resolvedCount = Number.isFinite(count) ? count : 0

  const { data, isLoading: listLoading, isFetching } = useQuery({
    queryKey: ['attention-work-day-entries', serviceInstanceId, jobId ?? 'all'],
    queryFn: () => assignmentsApi.getWorkDayEntries({
      needs_supervisor_attention: true,
      job_id: jobId ?? undefined,
      page_size: 100,
    }),
    enabled: open,
    staleTime: 30_000,
  })

  const entries = data?.results ?? []
  const listCount = data?.count ?? resolvedCount

  const assignmentJobIds = useMemo(
    () => [...new Set(entries.map((entry) => String(entry.job ?? '')).filter(Boolean))],
    [entries],
  )

  const assignmentsQuery = useQuery({
    queryKey: ['attention-entry-assignments', assignmentJobIds.join('|')],
    queryFn: async () => {
      const responses = await Promise.all(
        assignmentJobIds.map((jobId) => assignmentsApi.getAssignments({ job_id: jobId, page_size: 200 })),
      )
      return responses.flatMap((response) => response.results ?? [])
    },
    enabled: open && assignmentJobIds.length > 0,
    staleTime: 30_000,
  })

  const assignmentByWorkerKey = useMemo(() => {
    const map = new Map<string, { status?: string | null }>()
    for (const assignment of assignmentsQuery.data ?? []) {
      map.set(`${assignment.job_id}:${assignment.worker_id}`, assignment)
    }
    return map
  }, [assignmentsQuery.data])

  if (isLoading) {
    return <Skeleton className={`h-8 w-52 rounded-full ${className}`} />
  }

  if (resolvedCount <= 0) {
    return null
  }

  const workdaysHref = jobId
    ? withReturnTo(withServiceInstanceId(`/workdays?job=${encodeURIComponent(jobId)}`, serviceInstanceId), returnPath)
    : withReturnTo(withServiceInstanceId('/workdays', serviceInstanceId), returnPath)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-900 transition-colors hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60 ${className}`}
      >
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {resolvedCount} {labels.workDayEntryLower}{resolvedCount === 1 ? '' : 's'} need attention
        <ArrowRight className="h-3 w-3 shrink-0 opacity-70" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden p-0">
          <DialogHeader className="border-b border-border/70 px-5 py-4">
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4 text-rose-700" />
              Work day entries needing attention
            </DialogTitle>
            <DialogDescription>
              Flagged entries waiting for supervisor review before they can be completed.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
            {listLoading || isFetching ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <div className="flex min-h-28 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                <CalendarClock className="h-8 w-8 opacity-60" />
                <div>
                  <p className="text-sm font-medium text-foreground">No attention entries</p>
                  <p className="mt-0.5 text-xs">All {labels.workDayEntryPluralLower} are currently clear.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Showing {entries.length} of {listCount} flagged entr{listCount === 1 ? 'y' : 'ies'}.
                </p>
                {entries.map((entry) => (
                  <WorkDayEntryAttentionRow
                    key={entry.id}
                    entry={entry}
                    serviceInstanceId={serviceInstanceId}
                    returnPath={returnPath}
                    assignmentTerminal={isTerminalAssignmentStatus(
                      assignmentByWorkerKey.get(`${entry.job}:${entry.worker}`)?.status,
                    )}
                    onNavigate={() => setOpen(false)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border/70 px-5 py-3">
            <p className="text-xs text-muted-foreground">
              {listLoading ? (
                <span className="inline-flex items-center gap-1">
                  <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                  Loading entries...
                </span>
              ) : (
                `${listCount} entr${listCount === 1 ? 'y' : 'ies'} need supervisor attention`
              )}
            </p>
            <Button variant="outline" size="sm" className="h-8 rounded-md" asChild>
              <Link to={workdaysHref} onClick={() => setOpen(false)}>
                View {labels.workDayPluralLower}
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function WorkDayEntryAttentionRow({
  entry,
  serviceInstanceId,
  returnPath,
  assignmentTerminal = false,
  onNavigate,
}: {
  entry: WorkDayEntry
  serviceInstanceId?: string | null
  returnPath?: string | null
  assignmentTerminal?: boolean
  onNavigate: () => void
}) {
  const queryClient = useQueryClient()
  const jobId = String(entry.job ?? '')

  const clearAttentionMutation = useMutation({
    mutationFn: () => assignmentsApi.clearWorkDayEntryAttention(String(entry.id)),
    onSuccess: () => {
      toast.success('Supervisor attention cleared')
      queryClient.invalidateQueries({ queryKey: ['attention-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry-audits', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  return (
    <div className="rounded-lg border border-border/70 px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <JobTitleLink
              jobId={jobId}
              title={entry.job_title}
              serviceInstanceId={serviceInstanceId}
              returnPath={returnPath}
              className="truncate text-sm font-medium text-foreground hover:text-primary"
              onClick={onNavigate}
            />
            <Badge variant="outline" className="border-rose-200 bg-rose-50 px-1 py-0 text-[10px] text-rose-800">
              Needs attention
            </Badge>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {entry.worker_name || `Worker #${formatShortId(entry.worker)}`}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {entry.work_date ? `Work date ${formatDate(entry.work_date)}` : 'No work date'}
            {entry.status ? ` • ${getWorkDayEntryStatusLabel(entry.status)}` : ''}
            {entry.notes ? ` • ${entry.notes}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 rounded-md text-xs"
            disabled={clearAttentionMutation.isPending}
            onClick={() => clearAttentionMutation.mutate()}
          >
            {clearAttentionMutation.isPending ? (
              <Spinner className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
            )}
            Clear
          </Button>
          <Button variant="outline" size="sm" className="h-7 shrink-0 rounded-md text-xs" asChild={!assignmentTerminal} disabled={assignmentTerminal}>
            {assignmentTerminal ? (
              'Locked'
            ) : (
              <Link
                to={withReturnTo(withServiceInstanceId(`/workdays/${entry.id}/edit`, serviceInstanceId), returnPath)}
                onClick={onNavigate}
              >
                Review
              </Link>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
